import React, { useEffect, useState } from 'react';
import {
  submitWaitlist, getWaitlistStats, parseRefFromUrl, formatHeroCount,
  ROLE_OPTIONS,
} from './waitlistData';

export default function WaitlistHero({ headline, subhead }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [challenge, setChallenge] = useState('');
  const [refCode, setRefCode] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setRefCode(parseRefFromUrl());
    getWaitlistStats().then(setStats).catch(() => {});
    // Phase 5: prefill email if landing page passed it via ?email=...
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const prefill = params.get('email');
        if (prefill) setEmail(prefill.trim().toLowerCase());
      } catch (_) { /* ignore malformed query strings */ }
    }
  }, []);

  const canSubmit = email.trim() && name.trim() && role && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitWaitlist({
        email,
        name,
        role,
        biggest_challenge: challenge,
        referral_code: refCode,
      });
      // Redirect to confirmation with their code in the URL
      const code = result.referral_code;
      if (typeof window !== 'undefined') {
        window.location.href = `/waitlist/confirmed?ref=${code}`;
      }
    } catch (err) {
      setError(err.message || 'Could not save your signup — try again');
      setSubmitting(false);
    }
  };

  const total = stats?.total_count ?? null;
  const countText = total != null ? formatHeroCount(total) : 'Reserve your spot';

  return (
    <section id="waitlist-hero" className="wl-hero">
      <div className="wl-hero__inner">
        <div className="wl-hero__eyebrow">EARLY ACCESS</div>
        <h2 className="wl-hero__title">
          {headline || (<>Reserve your spot before <em>launch.</em></>)}
        </h2>
        <p className="wl-hero__sub">
          {subhead || 'AI work intelligence that meets you where you are. Be among the first to experience Curx AI.'}
        </p>

        <form className="wl-hero__form" onSubmit={handleSubmit}>
          {refCode && (
            <div className="wl-hero__ref-pill">
              Referred by code <strong>{refCode}</strong>
            </div>
          )}

          <div className="wl-hero__row">
            <label className="wl-hero__field">
              <span className="wl-hero__label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="wl-hero__input"
                required
                autoComplete="email"
              />
            </label>
            <label className="wl-hero__field">
              <span className="wl-hero__label">Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="wl-hero__input"
                required
                maxLength={120}
                autoComplete="name"
              />
            </label>
          </div>

          <label className="wl-hero__field">
            <span className="wl-hero__label">Your role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="wl-hero__input wl-hero__select"
              required
            >
              <option value="">Pick one...</option>
              {ROLE_OPTIONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>

          <label className="wl-hero__field">
            <span className="wl-hero__label">
              What&rsquo;s your biggest career challenge right now?
              <span className="wl-hero__opt">(optional)</span>
            </span>
            <textarea
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              placeholder="e.g., Stuck at Senior, want to break into Staff. Or: burnout from too many priorities."
              className="wl-hero__input wl-hero__textarea"
              rows={3}
              maxLength={200}
            />
            <span className="wl-hero__counter">{challenge.length}/200</span>
          </label>

          {error && (
            <div className="wl-hero__error" role="alert">{error}</div>
          )}

          <button
            type="submit"
            className="wl-hero__submit"
            disabled={!canSubmit}
          >
            {submitting ? 'Reserving...' : 'Reserve my spot →'}
          </button>

          <div className="wl-hero__count">{countText}</div>
          <div className="wl-hero__launch-badge">Launching soon</div>
        </form>
      </div>
    </section>
  );
}
